/**
 * `sync.studyGroups.*` — the public study-group API on top of the
 * per-group StudyGroupDo sync.
 *
 * Same shape as `sync.decks.deck(id).notes.*`: one entry point
 * (`sync.studyGroups`) surfaces the dashboard/discovery API, and
 * `sync.studyGroups.group(groupId)` returns a scoped facade for all
 * per-group reads + mutations.
 *
 * Read-only data comes from `useStudyGroupStore` (Zustand, per-group
 * slice). Mutations send JSON messages over the active group's Yjs
 * provider (`useStudyGroupDoSync` wires the socket;
 * `getGroupProviderSender` looks it up).
 *
 * The implementation is split per-concern: `meta.ts` / `members.ts` /
 * `folders.ts` / `decks.ts` / `activity.ts` (each concern's sub-API)
 * plus `scoped-deck.ts` (single-deck-within-a-group). Shared helpers
 * (`currentUserId`, `sendSG`, `stripCardForSG`, dashboard hooks) live
 * in `helpers.ts`.
 */

import type { SyncGroupMembership } from '@flashka/shared/sync-engine/schema/entities';
import type { DeckId, DeckPath } from '@flashka/shared/sync-engine/schema/shared-types';

import { poolAcquire, poolRelease } from '../internal/group-connection-pool';
import { getActiveStudyGroupDoc } from '../internal/hook/study-group-doc-registry';
import { enqueueForGroup } from '../internal/hook/study-group-sender-registry';
import { useFlashkaStore } from '../internal/store/flashka-store';
import { useStudyGroupStore } from '../internal/store/study-group-store';
import { createActivityApi } from './activity';
import { createDecksApi } from './decks';
import { createDrawSettingsApi } from './draw-settings';
import { createFoldersApi } from './folders';
import { useActiveGroupMemberships } from './helpers';
import { createLeaderboardApi } from './leaderboard';
import { createMembersApi } from './members';
import { createMetaApi } from './meta';
import type { SGScopedGroup } from './types';

export {
  isActiveGroupMember,
  pickActiveLinkedGroup,
  useActiveLinkedGroup,
  useIsActiveGroupMember,
} from './active-links';
export {
  type ActivityStatus,
  type ActivityTranslator,
  getActivityStatus,
  resolveMemberPresence,
} from './activity-status';
export {
  composeMetricRanking,
  composeRevealRows,
  leaderboardEntryScrollIndex,
} from './compose-leaderboard-rows';
export {
  type StudyGroupContextValue,
  StudyGroupProvider,
  useSGReturnParam,
  useStudyGroupContext,
} from './context';
export {
  type CopySGDeckToPersonalArgs,
  copySGDeckToPersonalLibrary,
  type CopySGDeckToPersonalResult,
} from './copy-deck-to-personal';
export {
  pickDominantGroupId,
  useDominantGroupBreakdown,
  useDominantGroupId,
  useSessionCardCountForDominant,
  useSessionMPForDominant,
  useTodayMPForDominant,
} from './dominant-group';
export { MIN_MEMBERS_FOR_STACK, studyGroupHeaderChips } from './group-header-chips';
export { buildInviteUrl } from './helpers';
export {
  type LeaderboardApi,
  type LiveScoreCache,
  type PushCaseStudySessionArgs,
  pushCaseStudySessionToAllGroups,
  type PushOralExamSessionArgs,
  pushOralExamSessionToAllGroups,
  type PushScoreArgs,
  pushScoreToAllGroups,
} from './leaderboard';
export {
  emitTrigger,
  fireEarlyExitToastNow,
  LEADERBOARD_STREAK_TOAST_STORAGE_KEY,
  type LeaderboardFetcher,
  type LeaderboardLifecycleAdapters,
  type LeaderboardLifecycleArgs,
  type LeaderboardLifecycleStorage,
  type LeaderboardToastAdapter,
  useLeaderboardSessionLifecycle,
} from './leaderboard-lifecycle';
export {
  createLeaderboardPrefsStore,
  type CreateLeaderboardPrefsStoreOptions,
  DEFAULT_LEADERBOARD_PERIOD,
  type LeaderboardMetric,
  type LeaderboardPrefsApi,
} from './leaderboard-prefs';
export {
  LEADERBOARD_PATH,
  leaderboardPeriodSchema,
  leaderboardResponseSchema,
  leaderboardRowSchema,
} from './leaderboard-rest';
export {
  type AddReviewEvent,
  type PerGroupAccumulator,
  readPostSnapshot,
  readSnapshot,
  type RevealSnapshot,
  type SessionState,
  useActiveSession,
  useActiveSessionFirstCardDone,
  useActiveSessionLastCardReached,
  useLeaderboardSessionStore,
  useSessionCardCountForGroup,
  useSessionMPForGroup,
} from './leaderboard-session-store';
export {
  getCanEditDeckMaterials,
  useCanEditDeckMaterials,
} from './materials-permissions';
export {
  type SeedMirrorArgs,
  seedMirrorIfMissing,
  seedOrMergeSGMirror,
  sgKey,
} from './mirror-seed';
export {
  type RemovableSharedGroup,
  removableSharedGroups,
  useRemovableSharedGroups,
} from './removable-groups';
export {
  buildLeaderboard,
  computePeriodTimeMs,
  EVENT_POINTS,
  eventPointsFor,
  type LeaderboardPeriod,
  type LeaderboardResponse,
  type LeaderboardRow,
  multiplierForStreak,
  ORAL_EXAM_SESSION_POOL_MP,
  type ScoreBreakdown,
  type ScoreEventType,
  scoreFor,
  scoreForOralExamSession,
  STREAK_MULTIPLIER_TABLE,
} from './scoring';
export {
  sgCopyMayReadLegacySource,
  type SGDeckCardSource,
  sgDeckCardSource,
  type SGDeckSharedMeta,
  sgDeckSharedMeta,
  sgDeckSources,
  sgMirrorDeckIdFor,
  sgSourcesWithheldFrom,
} from './sg-deck-card-source';
export {
  pendingSgDeckLoads,
  requestSgDeckCards,
  SG_DECK_CARDS_REQUEST_TIMEOUT_MS,
  type SGRequestDeckCardsMsg,
  useHydrateSgDeckCards,
} from './sg-deck-cards-load';
export { copySGDeckMaterials } from './sg-deck-copy-materials';
export type { SGGroupDeckRow, SGGroupDecksInput, SGMirrorRef } from './sg-group-decks';
export { buildSGGroupDeckRows, sgMirrorRefsForGroup } from './sg-group-decks';
export {
  clearActiveStudyTimeTracker,
  createStudyTimeTracker,
  type CreateStudyTimeTrackerOptions,
  getActiveStudyTimeTracker,
  setActiveStudyTimeTracker,
  STUDY_TIME_GRACE_MS,
  STUDY_TIME_SUSPEND_GAP_MS,
  type StudyTimeDisplayPart,
  studyTimeDisplayParts,
  type StudyTimeTracker,
  takeStudyTimeDeltaMsForWire,
} from './study-time';
export { flushStudyTimeToGroups } from './study-time-flush';
export {
  COMBO_VARIANT_POOL_SIZES,
  MILESTONE_VARIANT_POOL_SIZES,
  pickEarlyExitI18nKey,
  pickToastI18nKey,
  pickVariant,
  type ToastBadgeIcon,
  type ToastI18nKey,
} from './toast-i18n';
export {
  COMBO_TIERS,
  type ComboTier,
  decideToast,
  isStreakTier,
  MILESTONE_TIERS,
  type MilestoneTier,
  STREAK_TIERS,
  type StreakTier,
  type ToastDecision,
  type ToastDecisionEvent,
  type ToastGuards,
  type ToastTrigger,
} from './toast-rules';
export type {
  FolderMirrorPayload,
  MirroredFolder,
  SGScopedDeck,
  SGScopedGroup,
} from './types';
export {
  type LeaveGroupPorts,
  type LeaveGroupRequest,
  leaveStudyGroup,
  softDeleteMembershipLocally,
  useLeaveStudyGroup,
} from './use-leave-group';
export {
  useEnsureSGMirrorsForGroup,
  useSeedOrMergeSGMirror,
  useSeedOrMergeSGMirrorsForDecks,
  useSeedOrMergeSGMirrorsForGroup,
} from './use-seed-sg-mirror';

// ---------------------------------------------------------------------------
// sgGroup — scoped facade for one group
// ---------------------------------------------------------------------------

export function sgGroup(groupId: string): SGScopedGroup {
  return {
    id: groupId,
    meta: createMetaApi(groupId),
    drawSettings: createDrawSettingsApi(groupId),
    members: createMembersApi(groupId),
    folders: createFoldersApi(groupId),
    decks: createDecksApi(groupId),
    activity: createActivityApi(groupId),
    leaderboard: createLeaderboardApi(groupId),
    useIsConnected: () =>
      useStudyGroupStore((s) => s.groups[groupId]?.connectionStatus === 'connected'),
    acquire: () => poolAcquire(groupId),
    release: () => poolRelease(groupId),
  };
}

// ---------------------------------------------------------------------------
// findPersonalDeckIdByPath — lookup helper
// ---------------------------------------------------------------------------

/**
 * Resolve an `__sg_{gid}_{name}` mirror deckPath to the local `DeckId`.
 * Used by folder/study views that tag their cards with the mirror key
 * and need to route writes back through the scoped deck facade.
 */
export function findPersonalDeckIdByPath(
  deckPath: DeckPath | string,
): DeckId | undefined {
  const props = useFlashkaStore.getState().deckProperties;
  for (const [id, p] of Object.entries(props)) {
    if (p.deckPaths?.includes(deckPath as DeckPath)) return id as DeckId;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Public namespace
// ---------------------------------------------------------------------------

export const studyGroups = {
  /** Reactive list of the current user's group memberships. */
  use: useActiveGroupMemberships,
  /** Snapshot list. */
  get: () => {
    const st = useFlashkaStore.getState() as unknown as {
      groupMemberships?: SyncGroupMembership[];
    };
    return (st.groupMemberships ?? []).filter((m) => !m.deletedTimeISO);
  },
  /** Scoped facade for a single group. Cheap — no allocations beyond the returned object. */
  group: sgGroup,
  /**
   * Fire a raw message through the group's WebSocket. Used by callers
   * that want to route their own custom mutations (e.g. folder CRUD)
   * without waiting for a full typed builder.
   *
   * Routes through `enqueueForGroup`, the same buffered outbox every
   * other group mutation uses: if the socket isn't connected yet the
   * message is queued and flushed on connect, instead of being dropped.
   */
  send(groupId: string, msg: Record<string, unknown>): boolean {
    try {
      enqueueForGroup(groupId, JSON.stringify(msg));
      return true;
    } catch (err) {
      console.error('[sync.studyGroups.send] failed:', err);
      return false;
    }
  },
  /** Non-React: reach into the active group's Yjs doc. */
  getActiveDoc: getActiveStudyGroupDoc,
};
